import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Grid,
  Container,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import * as caseStudyService from "../../../services/CasestudyService";
import Notification from "../../../../../components/mui/Notification";
import debounce from "lodash/debounce";
import DeleteLoadingButton from "../../../../../components/DeleteLoadingButton";
import { ConfirmDialog } from "../../../../../components";
import { isEpmty } from "../../../../../helper/TextHelper";

export const Challenges = ({ caseStudyId, handleUpdatingStatus }) => {
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [loading, setLoading] = useState(true);
  const [problems, setProblems] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    caseStudyService
      .getProblemStory(caseStudyId)
      .then((res) => {
        setProblems(res.data);
      })
      .catch((e) => {
        console.error(e);
        if (e.response && e.response.status !== 404) {
          setNotify({
            isOpen: true,
            message: "Could not get problem stories. Please try again later",
            type: "error",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const updateStory = useCallback(
    (id, value) => {
      handleUpdatingStatus(true);

      const request = {
        id,
        text: value,
      };
      caseStudyService
        .updateProblemStory(request)
        .then((res) => {})
        .catch((e) => {
          console.error(e);
          setNotify({
            isOpen: true,
            message: "Could not update problem story. Please try again later",
            type: "error",
          });
        })
        .finally(() => {
          handleUpdatingStatus(false);
        });
    },
    [handleUpdatingStatus, setNotify]
  );

  const updateProblemTitle = useCallback(
    (id, value) => {
      handleUpdatingStatus(true);

      const request = {
        id,
        title: value,
      };

      caseStudyService
        .updateProblemTitle(request)
        .then((res) => {})
        .catch((e) => {
          console.error(e);
          setNotify({
            isOpen: true,
            message: "Could not update the title. Please try again later",
            type: "error",
          });
        })
        .finally(() => {
          handleUpdatingStatus(false);
        });
    },
    [handleUpdatingStatus]
  );

  // Debounced save function (memoized to keep reference stable)
  const debouncedStorySave = useMemo(
    () =>
      debounce((id, value) => {
        updateStory(id, value);
      }, 2000),
    [updateStory]
  );

  const debouncedProblemTitleSave = useMemo(
    () =>
      debounce((id, value) => {
        updateProblemTitle(id, value);
      }, 2000),
    [updateProblemTitle]
  );

  const handleProblemTitleChange = (id, index) => (event) => {
    const { value } = event.target;
    const newProblems = [...problems];
    const problem = newProblems[index];
    newProblems[index] = { ...problem, title: value };
    setProblems(newProblems);

    if (isEpmty(value)) {
      setErrors((prevValues) => ({
        ...prevValues,
        [id]: "Can not be empty. Please use the Delete button to delete the challenge.",
      }));
    } else {
      setErrors((prevValues) => ({
        ...prevValues,
        [id]: undefined,
      }));
      debouncedProblemTitleSave(id, value);
    }
  };

  const handleProblemStoryChange =
    (id, problemIndex, storyIndex) => (event) => {
      const { value } = event.target;
      const newProblems = [...problems];
      const stories = newProblems[problemIndex].stories;
      const changedStory = stories[storyIndex];

      stories[storyIndex] = { ...changedStory, text: value };
      setProblems(newProblems);

      if (isEpmty(value)) {
        setErrors((prevValues) => ({
          ...prevValues,
          [id]: "Can not be empty. Please use the Delete button to delete the challenge.",
        }));
      } else {
        setErrors((prevValues) => ({
          ...prevValues,
          [id]: undefined,
        }));
        debouncedStorySave(id, value);
      }
    };

  const onDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title:
        "Are you sure you want to delete this challenge along with all its associated paragraphs?",
      subTitle: "You can't undo this operation",
      onConfirm: () => deleteProblem(id),
    });
  };

  const deleteProblem = (id) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false,
    });
    caseStudyService
      .deleteProblem(id)
      .then((ignored) => {
        setNotify({
          isOpen: true,
          message: "Successfully deleted the problem.",
          type: "success",
        });
        const updatedList = problems.filter((q) => q.id !== id);
        setProblems(updatedList);
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not delete problem. Please try again later",
          type: "error",
        });
      });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Paper variant="outlined" sx={{ my: { xs: 1 }, p: { xs: 2 } }}>
        <Grid container spacing={3} sx={{ p: { xs: 1, md: 3 } }}>
          <Grid
            container
            spacing={0}
            justify="center"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Grid item>
              <Typography sx={{ fontWeight: "bold" }} variant="h6" gutterBottom>
                Challenges
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {loading
              ? [...Array(2)].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rounded"
                    height={60}
                    sx={{ m: 1 }}
                  />
                ))
              : problems.map((problem, problemIndex) => (
                  <Grid item xs={12} key={problem.id}>
                    <Paper
                      elevation={3}
                      key={problem.id}
                      variant="elevation"
                      sx={{ my: { xs: 1, sm: 2, md: 3 }, p: { xs: 2 } }}
                    >
                      <Grid container spacing={2} key={problemIndex}>
                        <Grid item xs={12}>
                          <TextField
                            key={problem.id}
                            name="title"
                            label="Title"
                            multiline
                            fullWidth
                            value={problem.title}
                            onChange={handleProblemTitleChange(
                              problem.id,
                              problemIndex
                            )}
                            error={!!errors[problem.id]}
                            helperText={errors[problem.id]}
                          />
                        </Grid>
                        {problem.stories?.map((story, storyIndex) => (
                          <>
                            <Grid item xs={12} key={story.id}>
                              <TextField
                                key={story.id}
                                name="text"
                                multiline
                                fullWidth
                                value={story.text}
                                onChange={handleProblemStoryChange(
                                  story.id,
                                  problemIndex,
                                  storyIndex
                                )}
                                error={!!errors[story.id]}
                                helperText={errors[story.id]}
                              />
                            </Grid>
                          </>
                        ))}
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          mt: 1,
                          display: "flex",
                          justifyContent: "right", // Pushes icon to the right
                          gap: 1,
                        }}
                      >
                        <DeleteLoadingButton
                          handleClick={() => onDelete(problem.id)}
                        />
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
          </Grid>
        </Grid>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Container>
  );
};
